/**
 * What every column on the leaderboard means, and how it got its value.
 *
 * Authored here rather than generated, because none of this prose exists anywhere
 * else — the harness knows how it computes `best_elo`, but nothing in the repo
 * says it in a sentence a reader who did not write the ranker can follow. That is
 * the whole gap this file fills.
 *
 * The rule it follows, and the one that keeps it from rotting: **no number and no
 * closed value set is written here.** Weights, tier bands, scope flags and the KG
 * vocabularies come out of `context/` at build time (`scripts/build-wiki.mjs`);
 * stage labels come from `lib/stages`, the map the run header renders; the
 * corroboration caveat is extracted from the Python constant that is its one home. Where a
 * column's values live somewhere else, `valuesFrom` points at them instead of
 * copying them. A second copy of the rubric here would be the exact failure
 * `context/target-criteria` exists to prevent — two rubrics that drift, with
 * nothing saying which one produced a given ranking.
 *
 * The three harness-owned vocabularies (`status`, `review_verdict`, dead-end
 * `verdict`, `context_source`) ARE written out here with `values`, because their
 * home is a Python literal and a Postgres column rather than a markdown table —
 * there is nothing to parse. Their doc comments in `api/types.ts` are the
 * reference; keep the two in step.
 */

/** Which surface a field appears on. Used only to group the page. */
export type Surface = 'run-header' | 'run-targets' | 'board' | 'dead-ends'

export interface ColumnDoc {
  /** The label as it appears in the UI, or the API field name where there is no header. */
  label: string
  /** The API field, when it differs from the label. Shown in mono. */
  field?: string
  surface: Surface
  /** What the value is. One or two sentences, plain language. */
  meaning: string
  /** Where it comes from — which agent, which tool, which computation. */
  computed: string
  /** A closed value set whose home is a Python literal, so nothing can parse it. */
  values?: { value: string; means: string }[]
  /** A closed value set that IS parsed or imported from its home. */
  valuesFrom?: 'tiers' | 'scopeFlags' | 'stages'
  /** A trap worth stating outright. Rendered as a warning, so use it sparingly. */
  caution?: string
  /**
   * A caution whose wording is single-sourced elsewhere and must not be
   * paraphrased. Resolved at render time from the generated payload.
   */
  cautionFrom?: 'runsIdentifiedNote' | 'championsNote' | 'recapNote'
}

export const SURFACE_LABELS: Record<Surface, string> = {
  'run-header': 'Run header — what the sweep did',
  'run-targets': 'This run — the ranked targets',
  board: 'Board — the cumulative cross-run view',
  // Not "Disqualified": only one of the three verdicts is a disqualification,
  // and the table's own headings say so. A section title that names the harshest
  // one describes the other two wrongly.
  'dead-ends': 'Not ranked — what review ruled on',
}

export const SURFACE_INTROS: Record<Surface, string> = {
  'run-header':
    'One sweep’s own record: when it ran, what it cost, and which instruction files it read. None of it is scientific output.',
  'run-targets':
    'One run’s ranking, immutable once written. Every hypothesis that survived review appears here, ranked — there is no top-N cut.',
  board:
    'One row per (protein, mechanism) across every run so far. A new sweep reads this before proposing, so it builds on previous ones rather than starting from zero.',
  'dead-ends':
    'Hypotheses review ruled out, admitted while still carrying gaps, or could not measure at all. Grouped by verdict rather than listed together, because the reader’s next move differs for each: keep the finding, take the missing measurement, or investigate an outage. A sweep that ranked nothing is exactly when this is the result rather than a footnote.',
}

export const COLUMN_DOCS: ColumnDoc[] = [
  // ── run header ────────────────────────────────────────────────────────────
  {
    label: 'Status',
    field: 'status',
    surface: 'run-header',
    meaning: 'How the sweep ended, independent of what it found.',
    computed:
      'Written by the harness at each lifecycle transition. `empty` is not a failure: the sweep ran and nothing survived review, which genuinely happens.',
    values: [
      { value: 'queued', means: 'Accepted, not yet started.' },
      { value: 'running', means: 'In flight. The stage says where.' },
      { value: 'succeeded', means: 'Finished and wrote at least one ranked target.' },
      {
        value: 'empty',
        means:
          'Finished and wrote none, with every data source it called answering normally — so this is a result. Either no hypothesis survived review, or the ranking failed to persist; the disqualified table below distinguishes the two.',
      },
      {
        value: 'blocked',
        means:
          'Finished and wrote none because a data source stopped answering. The message names the source and the share of calls that failed. Read nothing scientific into it: the hypotheses were never evaluated against complete evidence, so it says nothing about whether the search space is barren.',
      },
      { value: 'failed', means: 'Aborted on an error. The error text is shown in full under the header.' },
      { value: 'cancelled', means: 'Stopped by a person. The checkpoint survives, so the thread can be resumed.' },
      {
        value: 'salvaged',
        means:
          'The sweep raised — the error is shown in full under the header, same as `failed` — but a pool of hypotheses that had already cleared review was recovered and ranked after the fact. The ranking below is real; the run itself did not finish cleanly.',
      },
    ],
    caution:
      '`empty` and `blocked` were one status until a sweep wrote nothing because the knowledge graph’s database was down for fourteen of its fifteen minutes and the row reported that nothing had errored. They are split because the conclusions are opposite — one is evidence about the biology, the other is evidence about the infrastructure.',
  },
  {
    label: 'Stage',
    field: 'stage',
    surface: 'run-header',
    meaning: 'The last pipeline step the harness observed.',
    computed:
      'Updated as the supervisor dispatches. Dispatch is stat-driven rather than a fixed pipeline, so stages can repeat — three review/tournament cycles on one sweep is normal.',
    valuesFrom: 'stages',
  },
  {
    label: 'Est. cost',
    field: 'cost_usd',
    surface: 'run-header',
    meaning: 'What the sweep’s model calls cost, in US dollars.',
    computed:
      'Derived per request from the token counters, never stored: the price of a token is not a property of the run, so a persisted figure would silently become wrong the next time a rate changes. Correcting a price re-prices every historical run on the next reload.',
  },
  {
    label: 'Cache hits',
    field: 'cache_hit_rate',
    surface: 'run-header',
    meaning: 'The share of input tokens served from the model’s prompt cache.',
    computed: 'Cached input tokens over total input tokens, across every call in the run.',
    caution:
      'Shown beside the cost because it is what the cost turns on: a cache read bills at roughly a tenth of a fresh input token, so a change that cuts total tokens while dropping this rate can cost *more*.',
  },
  {
    label: 'Criteria version',
    field: 'context_version',
    surface: 'run-header',
    meaning:
      'Which instruction files this run read — the rubric, the program scope, the red flags, the glossary. A short content hash of the whole `context/` tree as it existed when the sweep started.',
    computed:
      'Hashed from the file contents the agents were about to read, not from what git or S3 held. Two runs showing the same version scored against the same rubric; two showing different versions did not, and a disputed ranking can say which.',
    caution:
      'A tree that could not be read at all records `unknown` rather than a plausible-looking hash. That is deliberate: an empty tree hashes to a perfectly valid-looking value, and the column exists to make a ranking reproducible, so it must not be the thing asserting the false part.',
  },
  {
    label: 'Criteria source',
    field: 'context_source',
    surface: 'run-header',
    meaning: 'Whether this run read the files the image shipped, or an override published to S3.',
    computed:
      'Recorded by the run-start sync. Git stays authoritative and CI mirrors `context/` on every deploy, but a scientist may overwrite one object to change a preference without a redeploy, and the next sweep picks it up.',
    values: [
      {
        value: 'baked',
        means:
          'The files committed to the repo, as built into the image. Also what a failed sync falls back to — a sync failure never aborts a sweep, so this value plus a warning in the runtime log is how a silently-ignored override shows up.',
      },
      {
        value: 's3',
        means:
          'The mutable mirror was applied over the baked tree. This page describes the *committed* tree, so where a run reads `s3` the two can differ; compare the version hash.',
      },
    ],
  },
  {
    label: 'Graph data',
    field: 'kg_build_date',
    surface: 'run-header',
    meaning:
      'When the knowledge graph this run read was built. The companion to the criteria version: that names the rubric a ranking was produced under, this names the data it was produced from.',
    computed:
      'Configured, not measured — the graph cannot be asked. `target_kg` has four tools and none answers this, and the ontology carries no version triple, so whoever rebuilds the graph sets `KG_BUILD_DATE` and every run of that deployment stamps it.',
    caution:
      'Not derived from the source bucket’s upload timestamps, though it looks like it could be: the newest upload is a lower bound on the build by an unknown margin, and an upload time is not a measurement time — a corrected re-upload moves it without the data changing, and a file uploaded today can hold decade-old observations.',
    values: [
      {
        value: 'unknown',
        means:
          'Nobody told this deployment, and the graph reports none. This is a finding rather than a missing field: the run may not state any post-build literature boundary at all, so a review that would turn on one says the boundary is undrawable instead of guessing a date.',
      },
      {
        value: 'YYYY-MM-DD',
        means:
          'Literature published after this date is outside the graph, so an absence in the KG is not evidence of absence for anything more recent — it has to be checked directly. It bounds the graph only; it says nothing about when the underlying measurements were made.',
      },
    ],
  },

  // ── this run's ranked targets ─────────────────────────────────────────────
  {
    label: '#',
    field: 'rank',
    surface: 'run-targets',
    meaning: 'Position in this run’s final ranking. 1 is the strongest candidate the sweep found.',
    computed:
      'Assigned by the ranker from the tournament result, after the last review cycle. A rank is within one run and means nothing across runs — use the board for that.',
  },
  {
    label: 'Protein',
    field: 'protein',
    surface: 'run-targets',
    meaning: 'The proposed target protein, as the run named it, with its accession where one was resolved.',
    computed:
      'Every hypothesis is anchored on an *Arabidopsis* or rice accession, because those are the two proteomes the knowledge graph scored. The bridge to the weeds is a separate, homology-based step and lives in the rationale.',
    caution:
      'Rice is also a crop. Essentiality measured there is a selectivity statement, not evidence of weed activity.',
  },
  {
    label: 'Elo',
    field: 'elo',
    surface: 'run-targets',
    meaning: 'Tournament rating from pairwise debates between hypotheses. Higher is better.',
    computed:
      'Every pair the ranker debated updates both ratings. Pairs whose rating gap is inside the noise floor are re-debated and only promoted on agreement, so a small difference between two neighbours is not a result.',
    caution:
      'Elo is comparative within a run. It says this hypothesis beat that one, not that either is a good target — the tier and the rubric score are what answer that.',
  },
  {
    label: 'BTL',
    field: 'btl_score',
    surface: 'run-targets',
    meaning:
      'Bradley–Terry–Luce strength: the same pairwise outcomes fitted as one model over all of them at once, rather than accumulated match by match.',
    computed:
      'Fitted from the full comparison matrix. Less sensitive than Elo to the order pairs happened to be debated in, which is why both are shown.',
  },
  {
    label: 'Tier',
    field: 'tier',
    surface: 'run-targets',
    meaning: 'The recommendation band the weighted rubric score falls in.',
    computed:
      // No axis count here. The rubric has six axes and this said seven, which is
      // the drift the file's own rule against authoring a number twice exists to
      // prevent — the count is in the generated payload and the rubric section
      // below renders it from there.
      'From the weighted rubric score. The bands and what they oblige are below, parsed from the rubric the ranker read.',
    valuesFrom: 'tiers',
    caution:
      'A tier is a recommendation to a person, so it has to carry its reason. "Conditional" with no named experiment is not usable output.',
  },
  {
    label: 'Pathway',
    field: 'pathway',
    surface: 'run-targets',
    meaning: 'The metabolic or signalling pathway the protein acts in.',
    computed:
      'From the knowledge graph’s pathway domain (AraCyc/OryzaCyc, MetaCyc, Plant Reactome). Blocking a step only matters if the pathway has no bypass, which is a separate field, not this one.',
  },
  {
    label: 'Ligand starting points',
    field: 'ligand_starts, ligand_start_count',
    surface: 'run-targets',
    meaning:
      'Co-crystallised ligands found in a structure of this protein, each with the compounds a catalog will sell that resemble it. The cheapest test the programme has: order the analog, spray it, see whether anything happens.',
    computed:
      'Stamped by the harness after the ranking is final, never narrated by a model: the accession is searched against the PDB, each entry’s ligands are classified, and every kept ligand’s SMILES goes to the internal compound-similarity search. No axis reads it and no weight changes — it is recorded, not scored, so it can never move a target up the ranking. It reaches a spreadsheet through the CSV export and the board column, not a column of this table.',
    caution:
      'A catalog hit is not activity, potency, selectivity or validation — it is a molecule someone will sell you. And a ligand may come from a homolog’s structure, in which case the pocket belongs to that organism’s protein and not to this one; the record names the organism, the identity and the coverage so the claim can be read at its real strength. The whole field is a snapshot of two external services at one moment, so a re-run can legitimately disagree with it.',
    values: [
      {
        value: 'exact',
        means:
          'The catalog compound is the co-crystallised ligand, or indistinguishable from it by the search. The band is the strongest thing the similarity number supports and still says nothing about whether it works on a plant.',
      },
      {
        value: 'close',
        means: 'A near neighbour — the same chemistry with substitutions.',
      },
      {
        value: 'analog',
        means:
          'A recognisable relative. Worth ordering on a well-precedented site and worth little on a novel one, which is why the bands are reported rather than a single cutoff being applied.',
      },
      {
        value: 'weak',
        means:
          'Reported for completeness because the search returned it. Not a starting point.',
      },
    ],
  },
  {
    label: 'Ligand lookup outcome',
    field: 'ligand_start_source',
    surface: 'run-targets',
    meaning:
      'Why the ligand column says what it says. A blank count means nothing looked; this field is the only thing that says why, and the two are read together or not at all.',
    computed:
      'Stamped on every row the sweep touched, on the failure paths as well as the success one. A count of `0` means the search ran and found nothing orderable — a finding. A blank count means it did not run.',
    values: [
      {
        value: 'measured',
        means: 'Structures searched, ligands classified, catalog answered. The count is the answer.',
      },
      {
        value: 'no_ligand',
        means:
          'Structures exist and none carried a ligand worth ordering an analog of — buffer, ions and the enzyme’s own cofactors do not count. Also a measurement, so the count is `0`.',
      },
      {
        value: 'no_structure',
        means:
          'Neither this protein nor a homolog above the identity and coverage floors has an experimental structure. Common and expected: only a small fraction of plant proteins have one.',
      },
      {
        value: 'no_accession',
        means:
          'The ranked row carries no accession, so there was nothing to search. An answer about the row, not about the protein.',
      },
      {
        value: 'structure_unreachable',
        means:
          'The structure database could not be read, or the target fell past the per-sweep cap. Says nothing about the protein.',
      },
      {
        value: 'catalog_unreachable',
        means:
          'Ligands were found and the compound search did not answer. The ligands stand; the orderable half of the answer is missing.',
      },
      {
        value: 'blank',
        means:
          'Recorded before this lookup existed, and never backfilled. It may not be read as “nothing orderable exists” — nothing has looked.',
      },
    ],
  },
  {
    label: 'Rationale',
    field: 'rationale',
    surface: 'run-targets',
    meaning:
      'The case for the target, in one paragraph: what the protein does, the strongest single piece of evidence with its source named, the weed bridge, what is unmeasured and what a measurement would change, the principal risk, and the next experiment.',
    computed:
      'Written by the report generator to a fixed template. The template is enforced at the tool boundary as well as in the prompt — a rationale that collapses into a bare list of null fields is logged as a warning naming the hypothesis, because an unenforced prompt rule is this harness’s most frequent silent failure.',
  },
  {
    label: 'Evidence',
    field: 'evidence',
    surface: 'run-targets',
    meaning: 'The citations and tool results behind the rationale, each with its type and identifier.',
    computed:
      'Collected as the hypothesis was built and reviewed. A field cited without its value and its source is not a citation — "the knowledge graph says" has discarded the only thing that made the claim checkable.',
  },
  {
    label: 'Review verdict',
    field: 'review_verdict',
    surface: 'run-targets',
    meaning: 'Whether this hypothesis entered the tournament clean, or entered it flagged.',
    computed:
      'Written by the harness from the same near-miss queue the review gate reads — never from the model’s own ranking payload.',
    values: [
      {
        value: '(no badge)',
        means: 'Passed review. Absence of a flag is the pass, so nothing is drawn rather than "unknown".',
      },
      {
        value: 'incomplete',
        means:
          'Too few of its decidable axes reached a verdict, and it competed carrying its gaps. It appears in this table *and* in the disqualified table below — not a contradiction: filtering it would make an unproven candidate indistinguishable from a barren run.',
      },
    ],
  },
  {
    label: 'Scope flag',
    field: 'scope_flag',
    surface: 'run-targets',
    meaning: 'The target is a type the program does not normally pursue, and was penalised for it rather than removed.',
    computed:
      'Set by the model against the program’s scope rules. Out-of-scope types cost points on druggability, so a flagged target sits low on the board instead of vanishing from it — a genuinely exceptional one should be findable.',
    valuesFrom: 'scopeFlags',
  },

  // ── board ─────────────────────────────────────────────────────────────────
  {
    label: 'Runs',
    field: 'runs_identified',
    surface: 'board',
    meaning: 'How many runs of this harness ranked this target.',
    computed:
      'Counted from the per-run rankings, one row per canonical (protein, mechanism) pair. Re-proposing a target a previous run already found is allowed — suppressing it would freeze every count at 1 forever.',
    // Verbatim from the API's own constant, never paraphrased: this is the field
    // most likely to be quoted out of context, and the Wiki is where someone
    // comes to find out what it means.
    cautionFrom: 'runsIdentifiedNote',
  },
  {
    label: 'Mechanism',
    field: 'mechanism_key',
    surface: 'board',
    meaning:
      'The proposed mode of action, normalised. Half of a board row’s identity: the same protein proposed by two different mechanisms is two rows, because they are two hypotheses.',
    computed:
      'Normalised by one function in the harness. Deliberately not normalised in SQL as well — two implementations would drift and split one target into two rows depending on which path wrote it.',
  },
  {
    label: 'Best Elo / Latest',
    field: 'best_elo, latest_elo',
    surface: 'board',
    meaning: 'The highest rating any run gave this target, and the rating the most recent run gave it.',
    computed: 'Aggregated across every run’s ranking.',
    caution:
      'Both are shown because either alone lies. Latest alone lets a shallow sweep bury a thorough one’s result; best alone ratchets upward forever, so a target later shown to be weaker keeps its old high score.',
  },
  {
    label: 'Best tier / Latest tier',
    field: 'best_tier, latest_tier',
    surface: 'board',
    meaning: 'The same disagreement as the Elo pair, in tier terms.',
    computed: 'The best and most recent tier assigned across runs.',
    valuesFrom: 'tiers',
  },
  {
    label: 'Distinct threads / prompts',
    field: 'distinct_threads, distinct_prompts',
    surface: 'board',
    meaning: 'How many separate conversations, and how many distinct launching briefs, the runs behind the count came from.',
    computed: 'Counted from the runs that contributed rows.',
    caution:
      'These sit beside the run count because they are what make it readable: five runs from one prompt is a weaker statement than two runs from two.',
  },
  {
    label: 'Aliases',
    field: 'aliases',
    surface: 'board',
    meaning: 'Every spelling any run used for this protein.',
    computed:
      'Collected while consolidating. The key is what the runs agreed on after normalisation, not what any single one of them wrote, so this is how you check that two names really are one target.',
  },
  {
    label: 'Champion Elo / rank',
    field: 'champion_elo, champion_rank',
    surface: 'board',
    meaning:
      'The rating and placing this target took in the most recent champions tournament it entered. A different quantity from the Elo pair above, not a newer version of it.',
    computed:
      'From a champions tournament: board rows above a tier floor are re-compared against each other in one pool, under one rubric digest, in a fresh context per comparison. The per-run Elo columns are left untouched, because a run’s rating is a property of the tournament that produced it.',
    caution:
      'A rank is only readable with the pool size beside it: first of two and first of twenty are different claims and the rank alone reads as the stronger one. The expanded row and the champions session carry both.',
  },
  {
    label: 'Champions rounds',
    field: 'champions_rounds',
    surface: 'board',
    meaning:
      'How many champions tournaments this target has been **entered into** — not how many it won.',
    computed:
      'Incremented once per tournament the target appears in, projected onto the board from the immutable champions log. Entry is decided by the tier floor and the pool cap, so it is a record of selection, not of performance.',
    // Verbatim from the API's own constant, for the same reason as the run count
    // below: this is a number a reader will want to quote, and the champions
    // version has to say one thing more — the pool was *selected* on the very
    // runs whose agreement it reports.
    cautionFrom: 'championsNote',
  },
  {
    label: 'Disqualified once',
    field: 'was_disqualified, disqualified_in_runs',
    surface: 'board',
    meaning:
      'Whether some run ruled this target out before it reached the board, and which runs did. The badge shows only when the answer is yes.',
    computed:
      'Set by a recap: a dead end whose re-review now passes joins the board through the ordinary path and is marked here, with the runs that had ruled it out listed. The dead-end row is never deleted — a target that vanished from the disqualified table would be indistinguishable from one no run ever ruled out.',
    values: [
      {
        value: 'true',
        means: 'At least one run disqualified it. Read the dead-end rows for that run to see on what.',
      },
      {
        value: 'false',
        means: 'A recap examined this target and found nothing against it.',
      },
      {
        value: 'blank',
        means:
          'No recap has looked. Not the same as clean, and the reason the field is three-state rather than a boolean.',
      },
    ],
    cautionFrom: 'recapNote',
  },
  {
    label: 'Ligand',
    field: 'ligand_start_count, latest_ligand_starts, ligand_starts_checked_at',
    surface: 'board',
    meaning:
      'The same ligand starting points as the run table, for the most recent run that looked. Sorting the board on it orders by what can be bought, which is not the same order as target quality and is not meant to be.',
    computed:
      'Projected from the newest contributing run rather than accumulated across runs, because the structure and catalog databases move: an old row’s answer is stale, not additional evidence. `ligand_starts_checked_at` is when that run asked, and the column should be read as of then.',
    caution:
      'This is the one number on the board that does not count runs of the harness. It is one lookup against two external databases, so the corroboration caveat that applies to every other column here does not apply to it — and neither does any claim that a compound works.',
  },
  {
    label: 'Last seen',
    field: 'last_seen_at',
    surface: 'board',
    meaning: 'When the most recent run to rank this target finished.',
    computed: 'From that run’s row. `run_ids` in the expanded detail is the full audit trail, newest first.',
  },

  // ── dead ends ─────────────────────────────────────────────────────────────
  {
    label: 'Verdict',
    field: 'verdict',
    surface: 'dead-ends',
    meaning:
      'Which of three different results this row records, and therefore which group of the table it appears under.',
    computed:
      'Written by the review gate at the moment it acted, not re-narrated afterwards by a model. The gate reads the review ledger the harness wrote — a verdict per hypothesis, passes included — and excludes or flags accordingly.',
    values: [
      {
        value: 'failed',
        means:
          'A supported disqualifier fired, and it is excluded from the tournament hard. This is a *result*, not a gap — a rediscovery of a commercial mode of action is a negative finding worth recording so it is not proposed again. Shown under **Disqualified**.',
      },
      {
        value: 'incomplete',
        means:
          'Too few decidable axes reached a verdict. Admitted to the tournament flagged, so it may also appear in the ranked table above. Shown under **Carried forward with gaps**.',
      },
      {
        value: 'unreviewed',
        means:
          'The harness tried twice and could not measure — a transient failure reaching a model or a data source. Admitted flagged, because failing open loudly beats deleting real work on a blip. Shown under **Not falsified**.',
      },
    ],
    caution:
      '`unreviewed` is deliberately not `incomplete`. One is an infrastructure failure and says nothing whatever about the hypothesis; the other is a measured outcome that says the record is thin. Reading the first as the second dresses an outage as evidence, in the direction that looks like science.',
  },
  {
    label: 'Axis',
    field: 'axis',
    surface: 'dead-ends',
    meaning: 'Which review axis fired the disqualifier. Empty for the other two verdicts: one is an absence of verdicts rather than any axis’s verdict, and the other never reached an axis at all.',
    computed:
      'Every axis is dispatched on every hypothesis. An axis that cannot decide from the record abstains, and abstention lowers the bar rather than counting against the hypothesis: a present `0` or `false` is a measurement, an absent field is an abstention.',
  },
  {
    label: 'Failing field',
    field: 'failed_check',
    surface: 'dead-ends',
    meaning:
      'The one field of the record the axis judged and found wanting — the answer to "why did this fail?" at the level someone can act on.',
    computed:
      'Taken from the reviewer’s own structured verdict, which has always been asked for it. It went unrecorded for the life of the repo, so every drop read as `unstated` and a hypothesis could be retired permanently on a reason nothing wrote down; rows from before that was fixed still say `unstated`, and that is a gap in the record rather than a finding about the hypothesis.',
    caution:
      'A disqualifier that names no field is worth less than it looks: `failed` writes a dead end, and a dead end suppresses re-proposal. A recap is how those hypotheses get a verdict that states its field.',
  },
  {
    label: 'What would settle it',
    field: 'would_settle_it',
    surface: 'dead-ends',
    meaning:
      'The measurement the reviewer says would decide the question — the work list, in the reviewer’s own words.',
    computed:
      'From the same structured verdict as the failing field. Present on any verdict the reviewer chose to state it on, including passes, so it is not only a consolation for a drop.',
  },
  {
    label: 'Verdict legibility',
    field: 'verdict_source',
    surface: 'dead-ends',
    meaning:
      'How much of the reviewer’s reply the harness could actually read. Shown on a row only when the answer is not "all of it".',
    computed:
      'Stamped by the harness while parsing, never sent by the model, and summarised least-trustworthy-wins across the axes — one illegible axis makes the whole verdict uncitable. A reply with no readable verdict object is not read as a failure at all; it abstains, which is what keeps a formatting problem from becoming a scientific finding.',
    values: [
      { value: 'json', means: 'The verdict parsed in full. This is the only value that makes a drop citable.' },
      {
        value: 'partial',
        means:
          'Some fields parsed and some did not. Treat the drop as a lead, not a result — a recap re-reviews it.',
      },
      {
        value: 'unparseable',
        means:
          'A verdict object was found but could not be read. Says nothing about the hypothesis.',
      },
      {
        value: 'absent',
        means: 'No verdict object at all. The axis abstained rather than failing the hypothesis.',
      },
      {
        value: 'blank',
        means:
          'Recorded before this was tracked, and never backfilled — inventing a value here is exactly the fabrication the traceability axis exists to catch. It may not be read as `json`.',
      },
    ],
  },
  {
    label: 'Gaps',
    field: 'gaps',
    surface: 'dead-ends',
    meaning: 'What was never measured. These are the fields to go and fill.',
    computed:
      'Named by the axes that abstained. Absence of data is a finding in this program — checking a domain and reporting it empty is worth as much as finding a value there, and it is what turns an undecidable axis into a decidable one next time.',
  },
]
